import { Component, input, output, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProjectService } from '../../../shared/services/project.service';
import { UserService } from '../../../shared/services/user.service';
import { ProjectManager, Project } from '../../../shared/models/project.model';
import { User } from '../../../shared/models/user.model';

@Component({
  selector: 'app-manage-project-managers',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './manage-project-managers.html',
  styleUrls: ['./manage-project-managers.scss']
})
export class ManageProjectManagersComponent {
  projectId = input.required<string>();
  projectName = input<string>('');
  close = output<void>();

  private projectService = inject(ProjectService);
  private userService = inject(UserService);

  managers = signal<ProjectManager[]>([]);

  /** Archived managers stay linked to the project but are hidden by default. */
  showArchivedManagers = false;

  /** Managers shown in the list: archived ones only when asked for. */
  get visibleManagers(): ProjectManager[] {
    return this.managers().filter(m => !m.user?.isArchived || this.showArchivedManagers);
  }

  get archivedManagersCount(): number {
    return this.managers().filter(m => !!m.user?.isArchived).length;
  }
  availableUsers = signal<User[]>([]);
  loading = signal(false);
  loadingUsers = signal(false);
  selectedUserId = '';
  errorMessage = signal<string>('');

  private currentUsers: User[] = [];
  private currentProject: Project | null = null;
  private loadedTenantId: string | null = null;
  private isInitialized = false; // Prevent duplicate initialization

  constructor() {
    // Single effect to load everything when projectId changes
    effect(() => {
      const id = this.projectId();
      if (id && !this.isInitialized) {
        this.isInitialized = true;
        this.initializeData();
      }
    });

    // Subscribe to projects to get tenant info and managers updates
    this.projectService.projects$.subscribe(projects => {
      const id = this.projectId();
      if (id && this.isInitialized) {
        const project = projects.find(p => p.projectId === id);
        if (project) {
          // Check if tenant changed
          const tenantChanged = this.currentProject?.tenantId !== project.tenantId;

          // Convert managers (always, even if empty array)
          const convertedManagers: ProjectManager[] = (project.managers || []).map(m => ({
            id: m.id,
            projectId: project.projectId!,
            userId: m.userId,
            user: m.user ? {
              id: m.user.id,
              name: m.user.name,
              firstName: m.user.firstName,
              email: m.user.email
            } : undefined
          }));

          // Check if managers actually changed by comparing IDs
          const currentManagerIds = this.managers().map(m => m.id).sort().join(',');
          const newManagerIds = convertedManagers.map(m => m.id).sort().join(',');
          const managersChanged = currentManagerIds !== newManagerIds;

          if (managersChanged) {
            console.log('🔄 [Managers] Managers changed:', {
              before: this.managers().length,
              after: convertedManagers.length,
              added: convertedManagers.filter(m => !this.managers().find(cm => cm.id === m.id)).length,
              removed: this.managers().filter(m => !convertedManagers.find(cm => cm.id === m.id)).length
            });
            this.managers.set(convertedManagers);
            this.updateAvailableUsers(this.currentUsers);
          }

          this.currentProject = project;

          // Only reload tenant users if tenant changed
          if (tenantChanged && project.tenantId && project.tenantId !== this.loadedTenantId) {
            this.loadTenantUsers(project.tenantId);
          }
        }
      }
    });
  }

  // Initialize all data in parallel for better performance
  private async initializeData() {
    console.log('🚀 [Managers] Initializing data...');
    this.loading.set(true);
    this.errorMessage.set('');

    try {
      // Get project from current subscription value or wait for it
      let project = this.currentProject;

      if (!project) {
        // If no project loaded yet, get it from observable
        const projects = await new Promise<Project[]>((resolve) => {
          const sub = this.projectService.projects$.subscribe(projects => {
            resolve(projects);
            sub.unsubscribe();
          });
        });
        project = projects.find(p => p.projectId === this.projectId()) ?? null;
      }

      if (!project) {
        console.warn('⚠️ [Managers] Project not found');
        return;
      }

      this.currentProject = project;

      // Load managers and tenant users in parallel
      const [managers] = await Promise.all([
        this.projectService.getProjectManagers(this.projectId()),
        project.tenantId ? this.loadTenantUsers(project.tenantId) : Promise.resolve()
      ]);

      // Convert and set managers
      const convertedManagers: ProjectManager[] = managers.map(m => ({
        id: m.id,
        projectId: m.projectId,
        userId: m.userId,
        user: m.user ? {
          id: m.user.id,
          name: m.user.name,
          firstName: m.user.firstName ?? undefined,
          email: m.user.email ?? undefined
        } : undefined
      }));

      this.managers.set(convertedManagers);
      this.updateAvailableUsers(this.currentUsers);

      console.log('✅ [Managers] Data loaded:', {
        managers: convertedManagers.length,
        availableUsers: this.currentUsers.length
      });
    } catch (e) {
      console.error('❌ [Managers] Failed to initialize data', e);
      this.errorMessage.set('Failed to load data');
    } finally {
      this.loading.set(false);
    }
  }

  // Load tenant users with caching (service has built-in cache)
  private async loadTenantUsers(tenantId: string): Promise<void> {
    if (tenantId === this.loadedTenantId) {
      console.log('⏭️ [Managers] Tenant users already loaded, skipping');
      return;
    }

    console.log(`📥 [Managers] Loading users for tenant: ${tenantId}`);
    this.loadingUsers.set(true);

    try {
      // This method uses cache internally (60s TTL)
      const tenantUsers = await this.userService.getActiveUsersByTenantId(tenantId);
      console.log(`✅ [Managers] Loaded ${tenantUsers.length} users (from cache or server)`);

      this.currentUsers = tenantUsers;
      this.loadedTenantId = tenantId;
      this.updateAvailableUsers(tenantUsers);
    } catch (e) {
      console.error('❌ [Managers] Failed to load tenant users', e);
    } finally {
      this.loadingUsers.set(false);
    }
  }

  async loadManagers() {
    console.log('🔄 [Managers] Reloading managers...');
    this.loading.set(true);
    this.errorMessage.set('');

    try {
      const managers = await this.projectService.getProjectManagers(this.projectId());

      const convertedManagers: ProjectManager[] = managers.map(m => ({
        id: m.id,
        projectId: m.projectId,
        userId: m.userId,
        user: m.user ? {
          id: m.user.id,
          name: m.user.name,
          firstName: m.user.firstName ?? undefined,
          email: m.user.email ?? undefined
        } : undefined
      }));

      this.managers.set(convertedManagers);
      this.updateAvailableUsers(this.currentUsers);
    } catch (e) {
      console.error('Failed to load managers', e);
      this.errorMessage.set('Failed to load managers');
    } finally {
      this.loading.set(false);
    }
  }

  private updateAvailableUsers(allUsers: User[]) {
    const managerUserIds = this.managers().map(m => m.userId);

    // Only exclude already assigned managers - tenant filtering is done server-side
    const filteredUsers = allUsers.filter(u => u.id && !managerUserIds.includes(u.id));

    this.availableUsers.set(filteredUsers);
  }

  async addManager() {
    const userId = this.selectedUserId;
    if (!userId) {
      this.errorMessage.set('Please select a user');
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    try {
      await this.projectService.addManager(this.projectId(), userId);
      // Real-time subscription will update via projects$ observable
      this.selectedUserId = '';
    } catch (e: any) {
      console.error('Failed to add manager', e);
      this.errorMessage.set(e.message || 'Failed to add manager');
    } finally {
      this.loading.set(false);
    }
  }

  async removeManager(projectManagerId: string) {
    if (!confirm('Are you sure you want to remove this manager?')) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    try {
      await this.projectService.removeManager(projectManagerId);
      // Real-time subscription will update via projects$ observable
    } catch (e: any) {
      console.error('Failed to remove manager', e);
      this.errorMessage.set(e.message || 'Failed to remove manager');
    } finally {
      this.loading.set(false);
    }
  }

  getManagerDisplayName(manager: ProjectManager): string {
    if (!manager.user) return 'Unknown User';
    const name = manager.user.name || '';
    const firstName = manager.user.firstName || '';
    return firstName ? `${firstName} ${name}` : name;
  }

  onClose() {
    this.close.emit();
  }
}
